/**
 * Read/write helpers for nested connector DSL `fields` trees.
 */

import { coerceLiteral } from './literalCoercion';

function isPlainObject(value) {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function cloneDeep(value) {
  return JSON.parse(JSON.stringify(value));
}

function classifyFieldsNode(node) {
  if (!node || !isPlainObject(node)) {
    return null;
  }

  if (Object.prototype.hasOwnProperty.call(node, 'fields')) {
    return typeof node.source === 'string' ? 'array' : 'object';
  }

  return 'leaf';
}

export function parseFieldsFromConfigText(configJsonText) {
  if (!configJsonText?.trim()) {
    return {};
  }

  const parsed = JSON.parse(configJsonText);

  if (!parsed?.fields || typeof parsed.fields !== 'object' || Array.isArray(parsed.fields)) {
    throw new Error('Config must include a "fields" object');
  }

  return parsed.fields;
}

export function fieldsToConfigText(fields, { connector, existingConfigText } = {}) {
  let base = {};

  if (existingConfigText?.trim()) {
    try {
      base = JSON.parse(existingConfigText);
    } catch {
      base = {};
    }
  }

  const result = {
    ...base,
    fields,
  };

  if (connector?.document_type) {
    result.target = connector.document_type;
  }

  if (connector?.key) {
    result.mappingId = connector.key;
  }

  return JSON.stringify(result, null, 2);
}

/**
 * Resolve a config node at a dot path within `fields`.
 */
export function getFieldsNodeAtPath(fields, path) {
  if (!fields || !path) {
    return null;
  }

  const segments = path.split('.').filter(Boolean);
  let current = fields;

  for (let i = 0; i < segments.length; i += 1) {
    const segment = segments[i];
    const node = current?.[segment];

    if (!node) {
      return null;
    }

    if (i === segments.length - 1) {
      return node;
    }

    const kind = classifyFieldsNode(node);
    if (kind !== 'object' && kind !== 'array') {
      return null;
    }

    current = node.fields;
  }

  return null;
}

function ensureParentChain(fields, segments) {
  let current = fields;

  for (let i = 0; i < segments.length - 1; i += 1) {
    const segment = segments[i];
    const existing = current[segment];

    if (!existing || classifyFieldsNode(existing) === 'leaf') {
      current[segment] = { fields: {} };
    } else if (!existing.fields) {
      current[segment] = { ...existing, fields: {} };
    }

    current = current[segment].fields;
  }

  return current;
}

function mergeLeafSpec(existing, patch, fieldPath = '') {
  const next = { ...(existing || {}) };

  if (patch.source !== undefined) {
    if (patch.source) {
      next.source = patch.source;
    } else {
      delete next.source;
    }
  }

  if (patch.defaultValue !== undefined) {
    if (patch.defaultValue === '' || patch.defaultValue === null) {
      delete next.default;
    } else {
      next.default = coerceLiteral(patch.defaultValue, fieldPath);
    }
  }

  return next;
}

export function setLeafSource(fields, path, source) {
  const next = cloneDeep(fields || {});
  const segments = path.split('.').filter(Boolean);
  const parent = ensureParentChain(next, segments);
  const leafKey = segments[segments.length - 1];
  const existing = parent[leafKey] || {};

  parent[leafKey] = mergeLeafSpec(existing, { source }, path);

  return next;
}

export function setLeafDefault(fields, path, defaultValue) {
  const next = cloneDeep(fields || {});
  const segments = path.split('.').filter(Boolean);
  const parent = ensureParentChain(next, segments);
  const leafKey = segments[segments.length - 1];
  const existing = parent[leafKey] || {};

  parent[leafKey] = mergeLeafSpec(existing, { defaultValue }, path);

  return next;
}

export function setArraySource(fields, path, source) {
  const next = cloneDeep(fields || {});
  const segments = path.split('.').filter(Boolean);
  const parent = ensureParentChain(next, segments);
  const arrayKey = segments[segments.length - 1];
  const existing = parent[arrayKey] || { fields: {} };

  parent[arrayKey] = {
    ...existing,
    source: source || '',
    fields: existing.fields || {},
  };

  if (!parent[arrayKey].source) {
    delete parent[arrayKey].source;
  }

  return next;
}

export function clearLeafMapping(fields, path) {
  const next = cloneDeep(fields || {});
  const segments = path.split('.').filter(Boolean);
  let current = next;

  for (let i = 0; i < segments.length - 1; i += 1) {
    const segment = segments[i];
    if (!current[segment]?.fields) {
      return next;
    }

    current = current[segment].fields;
  }

  const leafKey = segments[segments.length - 1];
  const existing = current[leafKey];

  if (!existing || classifyFieldsNode(existing) !== 'leaf') {
    return next;
  }

  const preserved = {};
  if (existing.pipeline) {
    preserved.pipeline = existing.pipeline;
  }

  if (existing.conditions) {
    preserved.conditions = existing.conditions;
  }

  if (existing.filter) {
    preserved.filter = existing.filter;
  }

  if (Object.keys(preserved).length > 0) {
    current[leafKey] = preserved;
  } else {
    delete current[leafKey];
  }

  return next;
}

export function clearArraySource(fields, path) {
  const next = cloneDeep(fields || {});
  const node = getFieldsNodeAtPath(next, path);

  if (!node || classifyFieldsNode(node) !== 'array') {
    return next;
  }

  const segments = path.split('.').filter(Boolean);
  const parent = ensureParentChain(next, segments);
  const arrayKey = segments[segments.length - 1];
  const existing = parent[arrayKey];

  parent[arrayKey] = {
    fields: existing.fields || {},
  };

  if (existing.filter) {
    parent[arrayKey].filter = existing.filter;
  }

  return next;
}

export function getLeafMapping(fields, path) {
  const node = getFieldsNodeAtPath(fields, path);

  if (!node || classifyFieldsNode(node) !== 'leaf') {
    return { source: '', defaultValue: undefined, hasTransforms: false };
  }

  const hasTransforms = Boolean(node.pipeline?.length) || Boolean(node.conditions?.length) || Boolean(node.filter);

  return {
    source: node.source || '',
    defaultValue: node.default,
    hasTransforms,
  };
}

export function getArrayMapping(fields, path) {
  const node = getFieldsNodeAtPath(fields, path);

  if (!node || classifyFieldsNode(node) !== 'array') {
    return { source: '', hasFilter: Boolean(node?.filter) };
  }

  return {
    source: node.source || '',
    hasFilter: Boolean(node.filter),
  };
}

export function isLeafConfigured(mapping) {
  return Boolean(mapping?.source) || (mapping?.defaultValue !== undefined && mapping?.defaultValue !== '');
}

export function isArrayConfigured(mapping) {
  return Boolean(mapping?.source);
}
